import { RobotApi } from 'api/RobotApi'
import { Button } from 'components/Button/Button'
import FavoritesOnlyChip from 'components/FavoritesOnlyChip/FavoritesOnlyChip'
import Loader from 'components/Loader/Loader'
import RadioButton from 'components/RadioButton/RadioButton'
import Title from 'components/Title/Title'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import clsx from 'clsx'
import SwitchStyled from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/SwitchStyled'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  acceptCodaDiagnosticGroups,
  deleteRobotNotificationPrefs,
  fetchNotificationPrefs,
  fetchNotificationPrefsMeta,
  fetchRobotNotificationPrefs,
  patchNotificationPrefs,
  putRobotNotificationPrefs,
  type NotificationPrefsMeta,
  type NotifyGroupMeta,
  type RobotNotificationPrefsMode,
  type UserNotificationPrefsPatch,
  type UserRobotNotificationPrefs,
} from 'services/notificationPrefs'
import { PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import type { IRobotInfo } from 'utils/interfaces'
import { snoozeUntilAtMinute } from 'utils/notificationSnoozeSync'
import {
  DEFAULT_QUIET_HOURS_END,
  DEFAULT_QUIET_HOURS_START,
  apiTimeToPickerDate,
  formatTimezoneLabel,
  guessBrowserTimezone,
  isLocalTimeInQuietHours,
  pickerDateToApiTime,
} from 'utils/notificationTimezones'
import { useFmsNotificationsContext } from 'context/FmsNotificationsContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import { dateTimePickerStyles } from 'utils/dateTimePickerStyles'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import { flagParam, optionalIntParam } from 'utils/urlQueryState'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './NotificationSettings.module.css'

const DEFAULT_SNOOZE_HOURS = 1

/** Synthetic prefs-UI master checkbox (not a Django group id). */
const UI_NOTIFY_ALL_ID = -1
/** Synthetic master for diagnostic Coda Groups (not a real group name). */
const UI_ERROR_ALL = '__all__'

type NotifyGroupRow = NotifyGroupMeta & { disabled?: boolean }

function thematicNotifyGroups(groups: NotifyGroupMeta[]): NotifyGroupMeta[] {
  // Legacy «Notify — All» (if still returned) is ignored — master is UI-only.
  return groups.filter(g => {
    const normalized = g.name.replace(/\s+/g, ' ').trim().toLowerCase()
    return (
      normalized !== 'notify — all' &&
      normalized !== 'notify - all' &&
      normalized !== 'fms notify — all' &&
      normalized !== 'fms notify - all'
    )
  })
}

/** Strip «Notify — » / «FMS Notify — » for prefs UI labels (Django group names stay unchanged). */
function notifyGroupDisplayName(name: string): string {
  const trimmed = name.replace(/\s+/g, ' ').trim()
  const withoutPrefix = trimmed.replace(/^(?:fms\s+)?notify\s*[—–-]\s*/i, '').trim()
  return withoutPrefix || trimmed
}

/** Gausium package is not selectable for Autonomics prefs (pushes not wired for Gausium robots yet). */
function isGausiumNotifyGroupName(name: string): boolean {
  const normalized = name.replace(/\s+/g, ' ').trim().toLowerCase()
  return (
    normalized === 'notify — gausium' ||
    normalized === 'notify - gausium' ||
    normalized === 'fms notify — gausium' ||
    normalized === 'fms notify - gausium'
  )
}

/** Thematic packages the user can toggle in Defaults / Autonomics robot prefs. */
function selectableThematicNotifyGroups(groups: NotifyGroupMeta[]): NotifyGroupMeta[] {
  return thematicNotifyGroups(groups).filter(g => !isGausiumNotifyGroupName(g.name))
}

/** Checked packages from the API; empty list stays empty (all off). Master «All» only if every thematic is on. */
function expandNotifySelectionForUi(stored: number[], groups: NotifyGroupMeta[]): number[] {
  const thematic = selectableThematicNotifyGroups(groups)
  if (!thematic.length) return []
  const thematicIds = thematic.map(g => g.id)
  const visibleStored = stored.filter(id => thematicIds.includes(id))
  const allOn = thematicIds.length > 0 && thematicIds.every(id => visibleStored.includes(id))
  return allOn ? [...visibleStored, UI_NOTIFY_ALL_ID] : visibleStored
}

/**
 * Master «All» checks/unchecks every selectable package; unchecking any thematic clears All.
 * Locked packages (Notify — Gausium) are never toggled.
 */
function toggleNotifyAllowSelection(
  prev: number[],
  groups: NotifyGroupMeta[],
  changedId: number,
  checked: boolean
): number[] {
  const thematicIds = selectableThematicNotifyGroups(groups).map(g => g.id)
  if (changedId !== UI_NOTIFY_ALL_ID && !thematicIds.includes(changedId)) {
    return prev
  }

  if (changedId === UI_NOTIFY_ALL_ID) {
    return checked ? [...thematicIds, UI_NOTIFY_ALL_ID] : []
  }

  let next = toggleId(
    prev.filter(id => id !== UI_NOTIFY_ALL_ID),
    changedId,
    checked
  )
  if (thematicIds.length > 0 && thematicIds.every(id => next.includes(id))) {
    next = [...next, UI_NOTIFY_ALL_ID]
  }
  return next
}

/** Persist selectable thematic ids; locked Gausium membership is kept (not toggled here). */
function notifyAllowIdsForSave(selected: number[], groups: NotifyGroupMeta[]): number[] {
  const thematicIds = selectableThematicNotifyGroups(groups).map(g => g.id)
  const thematicSelected = thematicIds.filter(id => selected.includes(id))
  const lockedIds = thematicNotifyGroups(groups)
    .filter(g => isGausiumNotifyGroupName(g.name))
    .map(g => g.id)
  return [...thematicSelected, ...lockedIds]
}

function expandErrorSelectionForUi(stored: string[], groups: string[]): string[] {
  if (!groups.length) return []
  const visibleStored = stored.filter(g => groups.includes(g))
  const allOn = groups.length > 0 && groups.every(g => visibleStored.includes(g))
  return allOn ? [...visibleStored, UI_ERROR_ALL] : visibleStored
}

function toggleErrorAllowSelection(prev: string[], groups: string[], changed: string, checked: boolean): string[] {
  if (changed === UI_ERROR_ALL) {
    return checked ? [...groups, UI_ERROR_ALL] : []
  }
  let next = toggleStr(
    prev.filter(g => g !== UI_ERROR_ALL),
    changed,
    checked
  )
  if (groups.length > 0 && groups.every(g => next.includes(g))) {
    next = [...next, UI_ERROR_ALL]
  }
  return next
}

function errorAllowGroupsForSave(selected: string[], groups: string[]): string[] {
  return groups.filter(g => selected.includes(g))
}

function toggleId(list: number[], id: number, checked: boolean): number[] {
  if (checked) return list.includes(id) ? list : [...list, id]
  return list.filter(x => x !== id)
}

function toggleStr(list: string[], value: string, checked: boolean): string[] {
  if (checked) return list.includes(value) ? list : [...list, value]
  return list.filter(x => x !== value)
}

function modeLabel(mode: RobotNotificationPrefsMode, t: (k: string) => string): string {
  if (mode === 'mute_all') return t('notification_settings.mode_mute_all')
  if (mode === 'custom') return t('notification_settings.mode_custom')
  return t('notification_settings.mode_inherit')
}

const NotificationSettings = () => {
  const { t } = useTranslation()
  const user = useUser()
  const isMobile = useIsMobile()
  const { applyMutedUntil, applyQuietHours, mutedUntil: sharedMutedUntil } = useFmsNotificationsContext()
  const isDeveloper = user?.state.audience === 'developer'
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [error, setError] = useState<string | null>(null)
  const [acceptingCodaGroups, setAcceptingCodaGroups] = useState(false)

  const [meta, setMeta] = useState<NotificationPrefsMeta | null>(null)
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [robotOverrides, setRobotOverrides] = useState<Record<number, UserRobotNotificationPrefs>>({})

  const [snoozeEnabled, setSnoozeEnabled] = useState(false)
  const [mutedUntil, setMutedUntil] = useState<Date | null>(null)
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false)
  const [quietHoursStart, setQuietHoursStart] = useState(DEFAULT_QUIET_HOURS_START)
  const [quietHoursEnd, setQuietHoursEnd] = useState(DEFAULT_QUIET_HOURS_END)
  const [quietHoursActive, setQuietHoursActive] = useState(false)
  const [inheritAllowNotifyGroupIds, setInheritAllowNotifyGroupIds] = useState<number[]>([])
  const [inheritAllowErrorGroups, setInheritAllowErrorGroups] = useState<string[]>([])

  const [url, setUrl] = useUrlQueryState({
    robot: optionalIntParam(),
    rfav: flagParam(),
  })
  const selectedRobotId = url.robot ?? null
  const favoritesOnly = url.rfav
  const favoriteRobotIdSet = useMemo(
    () => new Set(user?.state.favoriteRobotIds ?? []),
    [user?.state.favoriteRobotIds]
  )
  const hydratedRobotIdRef = useRef<number | undefined>(undefined)
  const [robotMode, setRobotMode] = useState<RobotNotificationPrefsMode>('inherit')
  const [allowNotifyGroupIds, setAllowNotifyGroupIds] = useState<number[]>([])
  const [allowErrorGroups, setAllowErrorGroups] = useState<string[]>([])

  const [robotSearch, setRobotSearch] = useState('')
  const snoozeSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const quietSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Skip draft restore in onClose when the picker closed via OK (onAccept). */
  const snoozePickerAcceptedRef = useRef(false)

  const localTimezoneLabel = useMemo(() => formatTimezoneLabel(guessBrowserTimezone()), [])

  const filteredRobots = useMemo(() => {
    const q = robotSearch.trim().toLowerCase()
    const matched = robots.filter(robot => {
      // Per-robot prefs only for Autonomics (Gausium / Asbis pushes not available in this UI yet).
      if (robot.vendor !== VENDOR.AUTONOMICS) return false
      if (favoritesOnly && !favoriteRobotIdSet.has(robot.id)) return false
      if (!q) return true
      return (
        robot.name.toLowerCase().includes(q) ||
        robot.serialNumber.toLowerCase().includes(q) ||
        String(robot.id).includes(q)
      )
    })
    return sortWithFavoritesFirst(matched, favoriteRobotIdSet)
  }, [robots, robotSearch, favoritesOnly, favoriteRobotIdSet])

  const hasFavoriteAutonomics = useMemo(
    () => robots.some(robot => robot.vendor === VENDOR.AUTONOMICS && favoriteRobotIdSet.has(robot.id)),
    [robots, favoriteRobotIdSet]
  )

  /** UI master «All» first, then thematic packages from API (Gausium locked). */
  const orderedNotifyGroups = useMemo((): NotifyGroupRow[] => {
    const thematic = thematicNotifyGroups(meta?.notify_groups || [])
    if (!thematic.length) return []
    return [
      {
        id: UI_NOTIFY_ALL_ID,
        name: t('notification_settings.notify_all_packages'),
        event_types: [] as string[],
        disabled: false,
      },
      ...thematic.map(group => ({
        ...group,
        disabled: isGausiumNotifyGroupName(group.name),
      })),
    ]
  }, [meta, t])

  const orderedErrorGroups = useMemo(() => {
    const groups = meta?.error_groups || []
    if (!groups.length) return [] as string[]
    return [UI_ERROR_ALL, ...groups]
  }, [meta])

  const errorGroupLabel = (group: string) =>
    group === UI_ERROR_ALL ? t('notification_settings.error_all_groups') : group

  const loadAll = useCallback(async () => {
    setLoadingProgress(PROGRESS_TYPES.WORK)
    setError(null)
    try {
      const [prefsData, metaData, overrides, robotsPage] = await Promise.all([
        fetchNotificationPrefs(),
        fetchNotificationPrefsMeta(),
        fetchRobotNotificationPrefs(),
        RobotApi.fetchRobots(1, 500, undefined, undefined, undefined, undefined, true),
      ])
      setMeta(metaData)
      setRobots(robotsPage.results)
      const map: Record<number, UserRobotNotificationPrefs> = {}
      for (const row of overrides) {
        map[row.robot_id] = row
      }
      setRobotOverrides(map)

      const until = prefsData.muted_until ? new Date(prefsData.muted_until) : null
      const active = Boolean(until && until.getTime() > Date.now())
      setSnoozeEnabled(active)
      setMutedUntil(active ? until : null)
      applyMutedUntil(prefsData.muted_until)

      setQuietHoursEnabled(Boolean(prefsData.quiet_hours_enabled))
      setQuietHoursStart(prefsData.quiet_hours_start || DEFAULT_QUIET_HOURS_START)
      setQuietHoursEnd(prefsData.quiet_hours_end || DEFAULT_QUIET_HOURS_END)
      setQuietHoursActive(Boolean(prefsData.quiet_hours_active))
      applyQuietHours({
        enabled: Boolean(prefsData.quiet_hours_enabled),
        start: prefsData.quiet_hours_start || DEFAULT_QUIET_HOURS_START,
        end: prefsData.quiet_hours_end || DEFAULT_QUIET_HOURS_END,
        timezone: prefsData.timezone || guessBrowserTimezone(),
      })

      const storedNotify = prefsData.allow_notify_group_ids || []
      const storedErrors = prefsData.allow_error_groups || []
      setInheritAllowNotifyGroupIds(expandNotifySelectionForUi(storedNotify, metaData.notify_groups || []))
      setInheritAllowErrorGroups(expandErrorSelectionForUi(storedErrors, metaData.error_groups || []))
      setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    } catch (e) {
      console.error(e)
      setError(e instanceof Error ? e.message : t('notification_settings.load_error'))
      setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  }, [t, applyMutedUntil, applyQuietHours])

  const onAcceptCodaGroups = async () => {
    setAcceptingCodaGroups(true)
    setError(null)
    try {
      await acceptCodaDiagnosticGroups()
      setMeta(await fetchNotificationPrefsMeta())
    } catch (e) {
      setError(e instanceof Error ? e.message : t('notification_settings.load_error'))
    } finally {
      setAcceptingCodaGroups(false)
    }
  }

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  useEffect(() => {
    const active = Boolean(sharedMutedUntil && sharedMutedUntil.getTime() > Date.now())
    setSnoozeEnabled(active)
    setMutedUntil(active ? sharedMutedUntil : null)
  }, [sharedMutedUntil])

  const selectedRobot = useMemo(
    () => robots.find(r => r.id === selectedRobotId) || null,
    [robots, selectedRobotId]
  )

  const selectionFromDefaults = useCallback((): { notify: number[]; errors: string[] } => {
    return {
      notify: [...inheritAllowNotifyGroupIds],
      errors: [...inheritAllowErrorGroups],
    }
  }, [inheritAllowNotifyGroupIds, inheritAllowErrorGroups])

  const robotHasSavedCustomAllowlist = (robotId: number): boolean => {
    const existing = robotOverrides[robotId]
    if (!existing || existing.mode !== 'custom') return false
    return (
      (existing.allow_notify_group_ids || []).length > 0 || (existing.allow_error_groups || []).length > 0
    )
  }

  const openRobot = (robot: IRobotInfo) => {
    if (robot.vendor !== VENDOR.AUTONOMICS) return
    setUrl({ robot: robot.id })
  }

  const closeRobot = () => {
    setUrl({ robot: undefined })
  }

  useEffect(() => {
    if (loadingProgress !== PROGRESS_TYPES.SUCCESS) return
    if (selectedRobotId == null) {
      hydratedRobotIdRef.current = undefined
      return
    }
    const robot = robots.find(r => r.id === selectedRobotId)
    if (!robot || robot.vendor !== VENDOR.AUTONOMICS) {
      if (robots.length > 0) setUrl({ robot: undefined })
      return
    }
    if (hydratedRobotIdRef.current === robot.id) return
    hydratedRobotIdRef.current = robot.id
    const override = robotOverrides[robot.id]
    const mode = override?.mode || 'inherit'
    setRobotMode(mode)
    if (mode === 'custom' && robotHasSavedCustomAllowlist(robot.id)) {
      setAllowNotifyGroupIds(
        expandNotifySelectionForUi(override?.allow_notify_group_ids || [], meta?.notify_groups || [])
      )
      setAllowErrorGroups(
        expandErrorSelectionForUi(override?.allow_error_groups || [], meta?.error_groups || [])
      )
    } else {
      const fromDefaults = selectionFromDefaults()
      setAllowNotifyGroupIds(fromDefaults.notify)
      setAllowErrorGroups(fromDefaults.errors)
    }
    setError(null)
  }, [
    loadingProgress,
    selectedRobotId,
    robots,
    robotOverrides,
    meta,
    selectionFromDefaults,
  ])

  const persistGlobal = useCallback(
    async (patch: UserNotificationPrefsPatch) => {
      setError(null)
      try {
        const updated = await patchNotificationPrefs(patch)
        if ('muted_until' in patch) {
          const until = updated.muted_until ? new Date(updated.muted_until) : null
          const active = Boolean(until && until.getTime() > Date.now())
          setSnoozeEnabled(active)
          setMutedUntil(active ? until : null)
          applyMutedUntil(updated.muted_until)
        }
        if ('quiet_hours_enabled' in patch || 'quiet_hours_start' in patch || 'quiet_hours_end' in patch) {
          setQuietHoursEnabled(Boolean(updated.quiet_hours_enabled))
          setQuietHoursStart(updated.quiet_hours_start || DEFAULT_QUIET_HOURS_START)
          setQuietHoursEnd(updated.quiet_hours_end || DEFAULT_QUIET_HOURS_END)
          setQuietHoursActive(Boolean(updated.quiet_hours_active))
          applyQuietHours({
            enabled: Boolean(updated.quiet_hours_enabled),
            start: updated.quiet_hours_start || DEFAULT_QUIET_HOURS_START,
            end: updated.quiet_hours_end || DEFAULT_QUIET_HOURS_END,
            timezone: updated.timezone || guessBrowserTimezone(),
          })
        }
      } catch (e) {
        console.error(e)
        setError(e instanceof Error ? e.message : t('notification_settings.save_error'))
      }
    },
    [t, applyMutedUntil, applyQuietHours]
  )

  const persistMutedUntil = useCallback(
    (until: Date | null) => {
      void persistGlobal({ muted_until: until ? until.toISOString() : null })
    },
    [persistGlobal]
  )

  const onSnoozeToggle = (checked: boolean) => {
    if (snoozeSaveTimerRef.current) {
      clearTimeout(snoozeSaveTimerRef.current)
      snoozeSaveTimerRef.current = null
    }
    if (checked) {
      const next = snoozeUntilAtMinute(new Date())
      next.setHours(next.getHours() + DEFAULT_SNOOZE_HOURS)
      setSnoozeEnabled(true)
      setMutedUntil(next)
      // Header must suppress sandwich immediately — do not wait for PATCH round-trip.
      applyMutedUntil(next.toISOString())
      persistMutedUntil(next)
    } else {
      setSnoozeEnabled(false)
      setMutedUntil(null)
      applyMutedUntil(null)
      persistMutedUntil(null)
    }
  }

  const onSnoozeUntilChange = (value: Date | null) => {
    // Draft only — do not apply/persist here. A past draft would clear shared snooze and
    // unmount this picker before the user presses OK.
    const next = value && !Number.isNaN(value.getTime()) ? snoozeUntilAtMinute(value) : value
    setMutedUntil(next)
  }

  const commitSnoozeUntil = useCallback(
    (value: Date | null) => {
      if (snoozeSaveTimerRef.current) {
        clearTimeout(snoozeSaveTimerRef.current)
        snoozeSaveTimerRef.current = null
      }
      const next = value && !Number.isNaN(value.getTime()) ? snoozeUntilAtMinute(value) : null
      const active = Boolean(next && next.getTime() > Date.now())
      if (!active) {
        setSnoozeEnabled(false)
        setMutedUntil(null)
        applyMutedUntil(null)
        persistMutedUntil(null)
        return
      }
      setSnoozeEnabled(true)
      setMutedUntil(next)
      applyMutedUntil(next!.toISOString())
      persistMutedUntil(next)
    },
    [applyMutedUntil, persistMutedUntil]
  )

  /** Discard uncommitted picker draft (Cancel / Escape) — restore last persisted snooze. */
  const restoreCommittedSnooze = useCallback(() => {
    const committed =
      sharedMutedUntil && sharedMutedUntil.getTime() > Date.now() ? sharedMutedUntil : null
    if (committed) {
      setSnoozeEnabled(true)
      setMutedUntil(committed)
      return
    }
    setMutedUntil(null)
  }, [sharedMutedUntil])

  /** Quiet-hours wall clock is local; sync browser IANA zone for server-side evaluation (no TZ UI). */
  const withLocalTimezone = useCallback(
    (patch: UserNotificationPrefsPatch): UserNotificationPrefsPatch => ({
      ...patch,
      timezone: guessBrowserTimezone(),
    }),
    []
  )

  const persistQuietHours = useCallback(
    (patch: UserNotificationPrefsPatch) => {
      if (quietSaveTimerRef.current) clearTimeout(quietSaveTimerRef.current)
      quietSaveTimerRef.current = setTimeout(() => {
        quietSaveTimerRef.current = null
        void persistGlobal(withLocalTimezone(patch))
      }, 400)
    },
    [persistGlobal, withLocalTimezone]
  )

  const onQuietHoursToggle = (checked: boolean) => {
    setQuietHoursEnabled(checked)
    const start = quietHoursStart || DEFAULT_QUIET_HOURS_START
    const end = quietHoursEnd || DEFAULT_QUIET_HOURS_END
    setQuietHoursStart(start)
    setQuietHoursEnd(end)
    const timezone = guessBrowserTimezone()
    applyQuietHours({ enabled: checked, start, end, timezone })
    setQuietHoursActive(
      isLocalTimeInQuietHours({ enabled: checked, start, end, timezone })
    )
    void persistGlobal(
      withLocalTimezone({
        quiet_hours_enabled: checked,
        quiet_hours_start: start,
        quiet_hours_end: end,
      })
    )
  }

  const onQuietStartChange = (value: Date | null) => {
    const api = pickerDateToApiTime(value)
    if (!api) return
    setQuietHoursStart(api)
    const end = quietHoursEnd || DEFAULT_QUIET_HOURS_END
    const timezone = guessBrowserTimezone()
    applyQuietHours({ enabled: quietHoursEnabled, start: api, end, timezone })
    setQuietHoursActive(
      isLocalTimeInQuietHours({ enabled: quietHoursEnabled, start: api, end, timezone })
    )
    persistQuietHours({
      quiet_hours_enabled: quietHoursEnabled,
      quiet_hours_start: api,
      quiet_hours_end: end,
    })
  }

  const onQuietEndChange = (value: Date | null) => {
    const api = pickerDateToApiTime(value)
    if (!api) return
    setQuietHoursEnd(api)
    const start = quietHoursStart || DEFAULT_QUIET_HOURS_START
    const timezone = guessBrowserTimezone()
    applyQuietHours({ enabled: quietHoursEnabled, start, end: api, timezone })
    setQuietHoursActive(
      isLocalTimeInQuietHours({ enabled: quietHoursEnabled, start, end: api, timezone })
    )
    persistQuietHours({
      quiet_hours_enabled: quietHoursEnabled,
      quiet_hours_start: start,
      quiet_hours_end: api,
    })
  }

  useEffect(() => {
    return () => {
      if (snoozeSaveTimerRef.current) clearTimeout(snoozeSaveTimerRef.current)
      if (quietSaveTimerRef.current) clearTimeout(quietSaveTimerRef.current)
    }
  }, [])

  const onInheritNotifyChange = (groupId: number, checked: boolean) => {
    const notifyGroups = meta?.notify_groups || []
    const next = toggleNotifyAllowSelection(inheritAllowNotifyGroupIds, notifyGroups, groupId, checked)
    setInheritAllowNotifyGroupIds(next)
    void persistGlobal({
      allow_notify_group_ids: notifyAllowIdsForSave(next, notifyGroups),
    })
  }

  const onInheritErrorChange = (group: string, checked: boolean) => {
    const errorGroups = meta?.error_groups || []
    const next = toggleErrorAllowSelection(inheritAllowErrorGroups, errorGroups, group, checked)
    setInheritAllowErrorGroups(next)
    void persistGlobal({
      allow_error_groups: errorAllowGroupsForSave(next, errorGroups),
    })
  }

  const persistRobot = useCallback(
    async (
      robotId: number,
      mode: RobotNotificationPrefsMode,
      notifySelected: number[],
      errorSelected: string[]
    ) => {
      const notifyGroups = meta?.notify_groups || []
      const errorGroups = meta?.error_groups || []
      const notifyPayload = notifyAllowIdsForSave(notifySelected, notifyGroups)
      const errorPayload = errorAllowGroupsForSave(errorSelected, errorGroups)
      if (mode === 'custom' && notifyPayload.length === 0 && errorPayload.length === 0) {
        setError(t('notification_settings.custom_requires_allowlist'))
        return
      }
      setError(null)
      try {
        if (mode === 'inherit') {
          await deleteRobotNotificationPrefs(robotId)
          setRobotOverrides(prev => {
            const next = { ...prev }
            delete next[robotId]
            return next
          })
        } else {
          const row = await putRobotNotificationPrefs(robotId, {
            mode,
            allow_notify_group_ids: mode === 'custom' ? notifyPayload : [],
            allow_error_groups: mode === 'custom' ? errorPayload : [],
          })
          setRobotOverrides(prev => ({
            ...prev,
            [robotId]: row,
          }))
        }
      } catch (e) {
        console.error(e)
        setError(e instanceof Error ? e.message : t('notification_settings.save_error'))
      }
    },
    [meta, t]
  )

  const onRobotModeChange = (mode: RobotNotificationPrefsMode) => {
    if (selectedRobotId == null) return
    let nextNotify = allowNotifyGroupIds
    let nextErrors = allowErrorGroups
    if (mode === 'custom' && !robotHasSavedCustomAllowlist(selectedRobotId)) {
      const fromDefaults = selectionFromDefaults()
      nextNotify = fromDefaults.notify
      nextErrors = fromDefaults.errors
      setAllowNotifyGroupIds(nextNotify)
      setAllowErrorGroups(nextErrors)
    }
    setRobotMode(mode)
    void persistRobot(selectedRobotId, mode, nextNotify, nextErrors)
  }

  const onRobotNotifyChange = (groupId: number, checked: boolean) => {
    if (selectedRobotId == null) return
    const next = toggleNotifyAllowSelection(allowNotifyGroupIds, meta?.notify_groups || [], groupId, checked)
    setAllowNotifyGroupIds(next)
    void persistRobot(selectedRobotId, robotMode, next, allowErrorGroups)
  }

  const onRobotErrorChange = (group: string, checked: boolean) => {
    if (selectedRobotId == null) return
    const next = toggleErrorAllowSelection(allowErrorGroups, meta?.error_groups || [], group, checked)
    setAllowErrorGroups(next)
    void persistRobot(selectedRobotId, robotMode, allowNotifyGroupIds, next)
  }

  if (loadingProgress === PROGRESS_TYPES.WORK || loadingProgress === PROGRESS_TYPES.IDLE) {
    return (
      <div className={style.panel}>
        <Loader />
      </div>
    )
  }

  if (selectedRobot) {
    return (
      <div className={style.panel} data-test-id={getTestId('notification-settings-robot')}>
        <div className={style.headerArea}>
          <button type="button" className={style.backLink} onClick={closeRobot}>
            ← {t('notification_settings.back_to_list')}
          </button>
          <div className={style.robotTitle}>{selectedRobot.name || selectedRobot.serialNumber}</div>
          <div className={style.robotMeta}>{selectedRobot.serialNumber}</div>
        </div>

        <div className={style.content}>
          <div className={style.section}>
            <div className={style.sectionTitle}>{t('notification_settings.robot_mode')}</div>
            <div className={style.sectionHint}>{t('notification_settings.robot_mode_hint')}</div>
            <div className={style.radioColumn}>
              <RadioButton
                label={t('notification_settings.mode_inherit')}
                checked={robotMode === 'inherit'}
                onChange={() => onRobotModeChange('inherit')}
              />
              <RadioButton
                label={t('notification_settings.mode_mute_all')}
                checked={robotMode === 'mute_all'}
                onChange={() => onRobotModeChange('mute_all')}
              />
              <RadioButton
                label={t('notification_settings.mode_custom')}
                checked={robotMode === 'custom'}
                onChange={() => onRobotModeChange('custom')}
              />
            </div>
          </div>

          {robotMode === 'custom' ? (
            <>
              <div className={style.section}>
                <div className={style.sectionTitle}>{t('notification_settings.allow_notify_groups')}</div>
                <div className={style.sectionHint}>{t('notification_settings.allow_notify_groups_hint')}</div>
                <div className={style.checkboxGrid}>
                  {orderedNotifyGroups.map(group => (
                    <FormControlLabel
                      key={group.id}
                      className={clsx(style.checkboxLabel, group.disabled && style.checkboxLabelDisabled)}
                      disabled={Boolean(group.disabled)}
                      title={
                        group.disabled ? t('notification_settings.notify_gausium_unavailable') : undefined
                      }
                      control={
                        <Checkbox
                          checked={!group.disabled && allowNotifyGroupIds.includes(group.id)}
                          disabled={Boolean(group.disabled)}
                          onChange={(_, checked) => onRobotNotifyChange(group.id, checked)}
                        />
                      }
                      label={notifyGroupDisplayName(group.name)}
                    />
                  ))}
                </div>
              </div>
              <div className={style.section}>
                <div className={style.sectionTitle}>{t('notification_settings.allow_error_groups')}</div>
                <div className={style.sectionHint}>{t('notification_settings.allow_error_groups_hint')}</div>
                <div className={style.checkboxGrid}>
                  {orderedErrorGroups.map(group => (
                    <FormControlLabel
                      key={group}
                      className={style.checkboxLabel}
                      control={
                        <Checkbox
                          checked={allowErrorGroups.includes(group)}
                          onChange={(_, checked) => onRobotErrorChange(group, checked)}
                        />
                      }
                      label={errorGroupLabel(group)}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {error ? <div className={style.errorText}>{error}</div> : null}
        </div>
      </div>
    )
  }

  return (
    <div className={style.panel} data-test-id={getTestId('notification-settings')}>
      <div className={style.headerArea}>
        <div className={style.titleRow}>
          <Title text={t('notification_settings.title')} />
        </div>
      </div>

      <div className={style.content}>
        {isDeveloper && meta?.error_groups_need_revision ? (
          <div className={style.revisionBanner} data-test-id={getTestId('notification-settings-groups-revision')}>
            <div className={style.revisionBannerTitle}>
              {t('notification_settings.diagnostic_groups_need_revision')}
            </div>
            <div className={style.revisionBannerMeta}>
              {(meta.error_groups_unknown_in_fms || []).length
                ? `${t('notification_settings.diagnostic_groups_unknown')}: ${(
                    meta.error_groups_unknown_in_fms || []
                  ).join(', ')}. `
                : ''}
              {(meta.error_groups_missing_from_coda || []).length
                ? `${t('notification_settings.diagnostic_groups_missing')}: ${(
                    meta.error_groups_missing_from_coda || []
                  ).join(', ')}.`
                : ''}
            </div>
            {(meta.error_groups_pending_accept || []).length ? (
              <div className={style.revisionBannerActions}>
                <Button.Standart
                  className={style.revisionBannerButton}
                  loading={acceptingCodaGroups}
                  disabled={acceptingCodaGroups}
                  dataTestId="notification-settings-accept-coda-groups"
                  onClick={() => void onAcceptCodaGroups()}
                >
                  {t('notification_settings.diagnostic_groups_accept')}
                </Button.Standart>
                <div className={style.revisionBannerMeta}>{t('notification_settings.diagnostic_groups_accept_hint')}</div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={style.section}>
          <div className={style.timeSettingsRow}>
            <div className={style.snoozeBlock}>
            <div className={style.row}>
              <span className={style.rowLabel}>{t('notification_settings.snooze')}</span>
              <SwitchStyled
                checked={snoozeEnabled}
                onChange={(_, checked) => onSnoozeToggle(checked)}
                inputProps={{ 'aria-label': t('notification_settings.snooze') }}
              />
            </div>
            {snoozeEnabled ? (
              <>
                <div className={style.sectionHint}>{t('notification_settings.snooze_hint')}</div>
                <div className={style.localTimezoneRow}>
                  <span className={style.localTimezoneCaption}>{t('notification_settings.local_timezone')}</span>
                  <span
                    className={style.timezoneChip}
                    title={t('notification_settings.local_timezone_hint')}
                    data-test-id={getTestId('notificationSettingsSnoozeTimezone')}
                  >
                    {localTimezoneLabel}
                  </span>
                </div>
                <div className={style.fieldBlock}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label={t('notification_settings.snooze_until')}
                      value={mutedUntil}
                      onChange={value => onSnoozeUntilChange(value)}
                      onAccept={value => {
                        snoozePickerAcceptedRef.current = true
                        commitSnoozeUntil(value)
                      }}
                      onClose={() => {
                        if (snoozePickerAcceptedRef.current) {
                          snoozePickerAcceptedRef.current = false
                          return
                        }
                        restoreCommittedSnooze()
                      }}
                      closeOnSelect={false}
                      ampm={false}
                      format="dd.MM.yyyy HH:mm"
                      slots={{ textField: TextField }}
                      slotProps={{
                        ...dateTimePickerStyles({ isMobile }),
                        actionBar: { actions: ['cancel', 'accept'] },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </>
            ) : null}
          </div>

          <div className={style.snoozeBlock}>
            <div className={style.row}>
              <span className={style.rowLabel}>{t('notification_settings.quiet_hours')}</span>
              <SwitchStyled
                checked={quietHoursEnabled}
                onChange={(_, checked) => onQuietHoursToggle(checked)}
                inputProps={{ 'aria-label': t('notification_settings.quiet_hours') }}
              />
            </div>
            {quietHoursEnabled ? (
              <>
                <div className={style.sectionHint}>{t('notification_settings.quiet_hours_hint')}</div>
                <div className={style.localTimezoneRow}>
                  <span className={style.localTimezoneCaption}>{t('notification_settings.local_timezone')}</span>
                  <span
                    className={style.timezoneChip}
                    title={t('notification_settings.local_timezone_hint')}
                    data-test-id={getTestId('notificationSettingsQuietTimezone')}
                  >
                    {localTimezoneLabel}
                  </span>
                </div>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div className={style.quietHoursTimes}>
                    <TimePicker
                      label={t('notification_settings.quiet_hours_start')}
                      value={apiTimeToPickerDate(quietHoursStart)}
                      onChange={onQuietStartChange}
                      ampm={false}
                      format="HH:mm"
                      views={['hours', 'minutes']}
                      slots={{ textField: TextField }}
                      slotProps={{
                        ...dateTimePickerStyles({
                          isMobile,
                          hideToolbar: true,
                          textField: {
                            inputProps: { 'data-test-id': getTestId('notificationSettingsQuietStart') },
                          },
                        }),
                        actionBar: { actions: [] },
                      }}
                    />
                    <TimePicker
                      label={t('notification_settings.quiet_hours_end')}
                      value={apiTimeToPickerDate(quietHoursEnd)}
                      onChange={onQuietEndChange}
                      ampm={false}
                      format="HH:mm"
                      views={['hours', 'minutes']}
                      slots={{ textField: TextField }}
                      slotProps={{
                        ...dateTimePickerStyles({
                          isMobile,
                          hideToolbar: true,
                          textField: {
                            inputProps: { 'data-test-id': getTestId('notificationSettingsQuietEnd') },
                          },
                        }),
                        actionBar: { actions: [] },
                      }}
                    />
                  </div>
                </LocalizationProvider>
              </>
            ) : null}
            {quietHoursEnabled && quietHoursActive ? (
              <div className={style.quietActiveHint}>{t('notification_settings.quiet_hours_active_now')}</div>
            ) : null}
          </div>
          </div>

          <div className={style.fieldBlock}>
            <div className={style.sectionTitle}>{t('notification_settings.allow_notify_groups_default')}</div>
            <div className={style.sectionHint}>
              {t('notification_settings.allow_notify_groups_default_hint')}
            </div>
            <div className={style.checkboxGrid}>
              {orderedNotifyGroups.map(group => (
                <FormControlLabel
                  key={group.id}
                  className={clsx(style.checkboxLabel, group.disabled && style.checkboxLabelDisabled)}
                  disabled={Boolean(group.disabled)}
                  title={
                    group.disabled ? t('notification_settings.notify_gausium_unavailable') : undefined
                  }
                  control={
                    <Checkbox
                      checked={!group.disabled && inheritAllowNotifyGroupIds.includes(group.id)}
                      disabled={Boolean(group.disabled)}
                      onChange={(_, checked) => onInheritNotifyChange(group.id, checked)}
                    />
                  }
                  label={notifyGroupDisplayName(group.name)}
                />
              ))}
            </div>
          </div>

          <div className={style.divider} role="separator" />

          <div className={style.fieldBlock}>
            <div className={style.sectionTitle}>{t('notification_settings.allow_error_groups_default')}</div>
            <div className={style.sectionHint}>
              {t('notification_settings.allow_error_groups_default_hint')}
            </div>
            <div className={style.checkboxGrid}>
              {orderedErrorGroups.map(group => (
                <FormControlLabel
                  key={group}
                  className={style.checkboxLabel}
                  control={
                    <Checkbox
                      checked={inheritAllowErrorGroups.includes(group)}
                      onChange={(_, checked) => onInheritErrorChange(group, checked)}
                    />
                  }
                  label={errorGroupLabel(group)}
                />
              ))}
            </div>
          </div>

          {error && selectedRobotId == null ? <div className={style.errorText}>{error}</div> : null}
        </div>

        <div className={style.divider} role="separator" />

        <div className={style.section}>
          <div className={style.sectionTitle}>{t('notification_settings.robots_section')}</div>
          <div className={style.sectionHint}>{t('notification_settings.robots_hint')}</div>

          <div className={style.filtersRow}>
            <input
              className={style.searchInput}
              type="text"
              placeholder={t('notification_settings.search_robots')}
              value={robotSearch}
              onChange={e => setRobotSearch(e.target.value)}
              data-test-id={getTestId('notification-settings-robot-search')}
            />
            <FavoritesOnlyChip pressed={favoritesOnly} onClick={() => setUrl({ rfav: !favoritesOnly })} />
          </div>

          {robots.length === 0 ? (
            <div className={style.empty}>{t('notification_settings.no_robots')}</div>
          ) : favoritesOnly && !hasFavoriteAutonomics ? (
            <div className={style.favoritesEmpty}>
              <div className={style.favoritesEmptyTitle}>{t('favorites.empty_robots')}</div>
              <div className={style.favoritesEmptyDescription}>{t('favorites.empty_analytics_description')}</div>
            </div>
          ) : filteredRobots.length === 0 ? (
            <div className={style.empty}>{t('notification_settings.no_robots_filtered')}</div>
          ) : (
            <div className={style.list}>
              <div className={style.listHeader}>
                <span>{t('notification_settings.col_serial')}</span>
                <span>{t('notification_settings.col_name')}</span>
                <span>{t('notification_settings.col_mode')}</span>
              </div>
              {filteredRobots.map(robot => {
                const mode = robotOverrides[robot.id]?.mode || 'inherit'
                return (
                  <div
                    key={robot.id}
                    className={style.listRow}
                    onClick={() => openRobot(robot)}
                    data-test-id={getTestId(`notification-settings-robot-${robot.id}`)}
                  >
                    <span className={style.listCell}>{robot.serialNumber}</span>
                    <span className={style.listCell}>{robot.name}</span>
                    <span className={style.listCell}>
                      <span
                        className={clsx(style.modeChip, {
                          [style.modeChipMute]: mode === 'mute_all',
                          [style.modeChipCustom]: mode === 'custom',
                        })}
                      >
                        {modeLabel(mode, t)}
                      </span>
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationSettings
